/**
 * One automatic reload per route, per tab session, for "the chunk this tab
 * wants is not on the server any more" failures.
 *
 * Hashed assets are served `immutable` for a year while index.html carries no
 * cache headers, so a long-lived tab can hold an index.html that points at
 * chunks a later deploy removed. Reloading picks up the current index.html.
 * The guard is what stops that from becoming a reload loop when the asset is
 * genuinely missing rather than stale.
 */

const guardKey = (): string => `chunkReloadAttempted:${window.location.pathname}`;

/** Returns true when the caller may reload; false when this route already tried. */
export const claimChunkReload = (): boolean => {
  try {
    if (sessionStorage.getItem(guardKey())) return false;
    sessionStorage.setItem(guardKey(), '1');
    return true;
  } catch {
    // Storage disabled or full — never reload, so we can't loop.
    return false;
  }
};

/** Called once the route renders, so a later stale chunk can retry too. */
export const releaseChunkReload = (): void => {
  try {
    sessionStorage.removeItem(guardKey());
  } catch {
    // ignore
  }
};

/** Vite / browser wording for a chunk that failed to load. */
export const isChunkLoadError = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string } | null;
  const text = `${err?.name ?? ''} ${err?.message ?? ''}`;
  return (
    /Failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /ChunkLoadError/i.test(text)
  );
};
